
// Utility functions for haptic feedback and touch responsiveness

/**
 * Provides haptic feedback if supported by the device
 * @param duration - Vibration duration in milliseconds
 */
export function vibrate(duration: number = 20) {
  if (navigator.vibrate) {
    navigator.vibrate(duration);
  }
}

/**
 * Provides a stronger haptic feedback for important events
 */
export function strongVibration() {
  if (navigator.vibrate) {
    navigator.vibrate([30, 20, 30]);
  }
}

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * @param func - The function to debounce
 * @param wait - Milliseconds to wait before invoking
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: number | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeout) {
      window.clearTimeout(timeout);
    }
    
    timeout = window.setTimeout(() => {
      func(...args);
    }, wait);
  };
}

/**
 * Adds a subtle pulse animation to an element on touch
 * @param element - DOM element to animate
 */
export function addTouchPulse(element: HTMLElement) {
  const handleTouchStart = () => {
    element.classList.add('touch-pulse');
    vibrate(15);
  };
  
  const handleTouchEnd = () => {
    element.classList.remove('touch-pulse');
  };
  
  element.addEventListener('touchstart', handleTouchStart);
  element.addEventListener('touchend', handleTouchEnd);
  element.addEventListener('touchcancel', handleTouchEnd);
  
  // Return cleanup function
  return () => {
    element.removeEventListener('touchstart', handleTouchStart);
    element.removeEventListener('touchend', handleTouchEnd);
    element.removeEventListener('touchcancel', handleTouchEnd);
  };
}
